import React from 'react'
import AnimatedElement from '../AnimatedElement'
import RevealText from '../TextReveal2'

function ScopeTemplate({
    title,
    subtitle,
    overview,
    points,
    tags = [],
    heroVariant = 'default',
}) {
    const isSpaceHero = heroVariant === 'space'

    return (
        <div className='relative overflow-hidden bg-[#edf2f7]'>
            <div className='absolute top-0 right-0 h-72 w-72 rounded-full bg-[#505da0]/10 blur-3xl'></div>
            <div className='absolute bottom-0 left-0 h-72 w-72 rounded-full bg-[#6d222e]/10 blur-3xl'></div>

            <div className='relative py-12 sm:py-14 md:py-16 px-4 sm:px-6 md:px-[8%] xl:px-[12%]'>
                
                {/* Heading for non-space pages */}
                {!isSpaceHero && (
                    <div className='mb-8 md:mb-10'>
                        <RevealText
                            text={title}
                            css='text-[3.2rem] max-sm:text-[2.2rem] font-bold poppins text-[#425472] leading-none'
                        />

                        {subtitle && (
                            <p className='mt-3 max-w-3xl text-[1.02rem] max-sm:text-[0.95rem] text-slate-600 leading-7'>
                                {subtitle}
                            </p>
                        )}
                    </div>
                )}

                {/* SPACE HERO */}
                {isSpaceHero ? (
                    <AnimatedElement direction='from-left'>
                        <div className='relative overflow-hidden rounded-[32px] border border-slate-700/60 bg-[radial-gradient(circle_at_top_left,_#2563eb_0%,_#0f172a_45%,_#020617_100%)] px-6 py-8 sm:px-8 md:px-10 md:py-10 shadow-[0_25px_60px_rgba(2,6,23,0.35)]'>
                            <div className='absolute -top-10 left-10 h-40 w-40 rounded-full bg-sky-400/20 blur-3xl'></div>
                            <div className='absolute bottom-0 right-0 h-52 w-52 rounded-full bg-indigo-500/10 blur-3xl'></div>

                            <span className='absolute left-[8%] top-[18%] h-1.5 w-1.5 rounded-full bg-white/80'></span>
                            <span className='absolute left-[18%] top-[30%] h-1 w-1 rounded-full bg-white/60'></span>
                            <span className='absolute left-[35%] top-[14%] h-1.5 w-1.5 rounded-full bg-white/70'></span>
                            <span className='absolute right-[20%] top-[18%] h-1 w-1 rounded-full bg-white/60'></span>
                            <span className='absolute right-[10%] top-[35%] h-1.5 w-1.5 rounded-full bg-white/80'></span>
                            <span className='absolute right-[28%] bottom-[18%] h-1 w-1 rounded-full bg-white/60'></span>
                            <span className='absolute left-[12%] bottom-[20%] h-1.5 w-1.5 rounded-full bg-white/70'></span>

                            <div className='relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center'>
                                <div>
                                    <h1 className='mt-3 text-[2.6rem] max-sm:text-[2rem] font-bold poppins text-white leading-none'>
                                        {title}
                                    </h1>

                                    {subtitle && (
                                        <p className='mt-4 text-[1.05rem] max-sm:text-[0.95rem] text-slate-300 leading-7 max-w-2xl'>
                                            {subtitle}
                                        </p>
                                    )}

                                    <p className='mt-5 text-[16px] max-sm:text-[15px] leading-8 max-sm:leading-7 text-slate-200 max-w-3xl'>
                                        {overview}
                                    </p>

                                    {tags.length > 0 && (
                                        <div className='mt-6 flex flex-wrap gap-3'>
                                            {tags.map((tag, index) => (
                                                <span
                                                    key={index}
                                                    className='rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[12px] tracking-wide text-white'
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className='flex justify-center'>
                                    <div className='relative h-[280px] w-[280px] max-sm:h-[220px] max-sm:w-[220px]'>
                                        <div className='absolute inset-0 rounded-full border border-white/10'></div>
                                        <div className='absolute inset-6 rounded-full border border-sky-300/20'></div>
                                        <div className='absolute inset-12 rounded-full border border-indigo-300/20'></div>

                                        <div className='absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-sky-300 to-blue-500 shadow-[0_0_40px_rgba(96,165,250,0.5)]'></div>

                                        <div className='absolute left-[20%] top-[25%] h-4 w-4 rounded-full bg-white shadow-[0_0_18px_rgba(255,255,255,0.45)]'></div>
                                        <div className='absolute right-[18%] top-[35%] h-3 w-3 rounded-full bg-sky-300 shadow-[0_0_18px_rgba(125,211,252,0.45)]'></div>
                                        <div className='absolute bottom-[18%] left-[28%] h-5 w-5 rounded-full bg-indigo-300 shadow-[0_0_18px_rgba(165,180,252,0.45)]'></div>

                                        <div className='absolute inset-x-10 top-1/2 h-px -translate-y-1/2 bg-white/10'></div>
                                        <div className='absolute inset-y-10 left-1/2 w-px -translate-x-1/2 bg-white/10'></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AnimatedElement>
                ) : (
                    <div className='grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch'>
                        <div className='lg:col-span-8'>
                            <AnimatedElement direction='from-left'>
                                <div className='h-full rounded-[28px] border border-slate-200 bg-white p-6 sm:p-7 md:p-8 shadow-[0_15px_40px_rgba(15,23,42,0.06)]'>
                                    <div className='flex items-center gap-3 mb-5'>
                                        <div className='h-11 w-11 rounded-2xl bg-gradient-to-br from-[#6d222e] to-[#505da0] shadow-md'></div>
                                        <div>
                                            <p className='text-[12px] tracking-[0.28em] text-slate-500 font-semibold'>
                                                Overview
                                            </p>
                                            <h2 className='text-[1.35rem] max-sm:text-[1.15rem] font-semibold text-[#1f2f46]'>
                                                Technical Scope
                                            </h2>
                                        </div>
                                    </div>

                                    <p className='text-[17px] max-sm:text-[15px] leading-8 max-sm:leading-7 text-slate-700'>
                                        {overview}
                                    </p>
                                </div>
                            </AnimatedElement>
                        </div>
                        <div className='lg:col-span-4'>
                            <AnimatedElement direction='from-right'>
                                <div className='h-full rounded-[28px] bg-[#0f1f33] p-6 sm:p-7 text-white shadow-[0_18px_45px_rgba(15,23,42,0.14)] relative overflow-hidden'>
                                    <div className='absolute top-0 right-0 h-40 w-40 rounded-full bg-white/5 blur-2xl'></div>

                                    <div className='relative'>
                                        <p className='text-[12px] tracking-[0.28em] text-white/60 font-semibold'>
                                            Technical Scope
                                        </p>
                                        <h3 className='mt-2 text-[1.45rem] font-semibold leading-snug'>
                                            Key Areas
                                        </h3>
                                        {subtitle && (
                                            <p className='mt-3 text-[15px] leading-7 text-slate-300'>
                                                {subtitle}
                                            </p>
                                        )}

                                        {tags.length > 0 && (
                                            <div className='mt-6 flex flex-wrap gap-2'>
                                                {tags.map((tag, index) => (
                                                    <span
                                                        key={index}
                                                        className='rounded-full border border-white/15 bg-white/10 px-3 py-2 text-[12px] tracking-wide text-white'
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </AnimatedElement>
                        </div>
                    </div>
                )}

                {/* Capabilities */}
                <div className='mt-20 md:mt-24'>
                    <AnimatedElement direction='from-left'>
                        <div className='mb-6'>
                            <h2 className='mt-2 text-[2rem] max-sm:text-[1.5rem] font-bold poppins text-[#1f2f46]'>
                                Capabilities
                            </h2>
                            <div className='mt-3 h-1 w-24 rounded-full bg-gradient-to-r from-[#6d222e] to-[#505da0]'></div>
                        </div>
                    </AnimatedElement>

                    <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
                        {points.map((point, index) => (
                            <AnimatedElement
                                key={index}
                                direction={index % 2 === 0 ? 'from-left' : 'from-right'}
                            >
                                <div className='group h-full rounded-[22px] border border-slate-200 bg-white p-5 md:p-6 shadow-[0_10px_30px_rgba(15,23,42,0.05)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,23,42,0.09)]'>
                                    <div className='flex items-start gap-4'>
                                        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#6d222e] to-[#505da0] text-white text-[15px] font-semibold shadow-md'>
                                            {String(index + 1).padStart(2, '0')}
                                        </div>

                                        <div className='pt-1'>
                                            <p className='text-[16px] max-sm:text-[15px] leading-7 text-slate-700'>
                                                {point}
                                            </p>
                                            <div className='mt-4 h-[3px] w-14 rounded-full bg-gradient-to-r from-[#6d222e] to-[#505da0]'></div>
                                        </div>
                                    </div>
                                </div>
                            </AnimatedElement>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ScopeTemplate